import { ReactNode, useLayoutEffect } from 'react';
import { UnifiedThemeProvider } from '@backstage/theme';
import { UnifiedTheme } from '@backstage/theme';

/**
 * Keeps the `.dark` class on <html> in step with the active Backstage theme.
 *
 * The MUI theme and `tokens.css` describe the same two modes from different
 * sides: the theme object styles the React tree, the stylesheet styles what the
 * theme cannot reach — above all the TechDocs reader, which renders MkDocs' own
 * HTML. The class is the single switch that keeps them agreeing; without it a
 * reader who picks the dark theme gets a dark shell around light docs.
 *
 * `useLayoutEffect` rather than `useEffect` so the class lands before the
 * browser paints and the page does not flash the other mode on a theme change.
 */
export const RevenexxThemeProvider = ({
  theme,
  isDark,
  children,
}: {
  theme: UnifiedTheme;
  isDark: boolean;
  children: ReactNode;
}) => {
  useLayoutEffect(() => {
    const { classList } = document.documentElement;
    classList.toggle('dark', isDark);
    // Tells the browser which scrollbars and form controls to draw, so the
    // built-in widgets follow the theme without being restyled by hand.
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  }, [isDark]);

  return <UnifiedThemeProvider theme={theme}>{children}</UnifiedThemeProvider>;
};
